import React, {
    useRef,
    useEffect,
    useState,
    useCallback,
    useMemo,
} from "react";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { useDispatch, useSelector } from "react-redux";
import { useSnackbar } from "notistack";
import JoditEditor from "jodit-react";
import { createQuotation, downloadQuotationFile, fetchAllQuotations, sendQuotationToClient } from "../../../../store/slices/generateQuotationSlice";
import { fetchAllQuotationTemplates } from "../../../../store/slices/QuotationTemplateSlice";
import { fetchCompanyProfiles } from "../../../../store/slices/companyProfileSlice";
import { fetchAllProjects } from "../../../../store/slices/projectsSlice";
import { fetchLeads } from "../../../../store/slices/leadsSlice";
import { fetchOtherExpenses } from "../../../../store/slices/otherexpenses";
import { getFilesByLead } from "../../../../store/slices/FileDataSlice";
import AdminLoader from "../../../../components/common/loader/AdminLoader";
import FilesViewer from "../../../lead/FilesViewer";
import FileViewer from "../../../Operations/SalesOperations/SalesFinance/CreateQuotation/FileViewer";
import CommaConvert from "../../../../utils/CommaConvert";
import { useCurrencySymbol } from "../../../../utils/currencyUtils";
import CloseIcon from "@mui/icons-material/Close";
import configApi from "../../../../config/api";
import {
    attachCrmHardwareTableEditorHandlers,
    buildCrmHardwareDetailsSectionHtml,
    isFocusedCrmQuotationEditableTableInput,
} from "../../../../utils/crmQuotationHardwareEditor";

const QuotationWizardBasePlan = ({
    selectedProject,
    setShowQuoteModal,
    calculatedFinanceProjects
}) => {
    const dispatch = useDispatch();
    const currency = useCurrencySymbol();
    const USAComma = CommaConvert();
    const { enqueueSnackbar } = useSnackbar();
    const createdProject = useSelector((state) => state.wizardstep.createdProject);
    const [isTemplateOpen, setIsTemplateOpen] = useState(false);
    const [isHistoryOpen, setisHistoryOpen] = useState(false);
    const [, setAssignedGroup] = useState("All");
    const [template, settemplate] = useState([]);
    const { templates } = useSelector((state) => state.quotationTemplates);
    const [content, setContent] = useState(""); // Changed from value to content
    const { profiles } = useSelector((state) => state.companyProfile);
    const { leads } = useSelector((state) => state.leads);
    const [selectedLead, setSelectedLead] = useState(null);
    const [selectedTemplate, setselectedTemplate] = useState("");
    const [replacedData, setReplacedData] = useState("");
    const [isLoading, SetisLoading] = useState(false);
    const [Templateid, setTemplateid] = useState(null);
    const [leadid, setleadid] = useState(null);
    const [fileid, setfileid] = useState(null);
    const [sendid, setsendid] = useState(null);
    const [downloadid, setdownloadid] = useState(null);
    const { quotations } = useSelector((state) => state.generateQuotation);
    const [, sethistoryid] = useState("");
    const [files, setFiles] = useState();
    const [fileName, sefileName] = useState([]);
    const [showfile, seshowfile] = useState([]);
    const [quotation, sequotations] = useState([]);
    const [editcontent, seteditContent] = useState(""); // Changed from value to content
    const [IsEdit, setIsEdit] = useState(false);
    const [setMailQuotation, setsetMailQuotation] = useState(null);
    const [showEditPreview, setShowEditPreview] = useState(false);
    const [editedTemplate, setEditedTemplate] = useState("");
    const [showVersionHistoryHint, setShowVersionHistoryHint] = useState(false);

    const resolvedCompanyLogoUrl = useMemo(() => {
        const profile = profiles?.[0];
        // Sidebar footer uses `profiles[0].logoPath`
        const raw = String(
            profile?.logoPath || profile?.companylogo || profile?.companyLogo || ""
        ).trim();
        if (!raw) return "";

        let absoluteUrl = raw;
        const isAbsolute =
            /^https?:\/\//i.test(raw) || /^data:/i.test(raw) || /^blob:/i.test(raw);
        if (!isAbsolute) {
            const backendUrl = String(configApi?.backendUrl || "").trim();
            const backendOrigin = backendUrl
                .replace(/\/api\/?$/i, "")
                .replace(/\/$/, "");
            const normalizedPath = raw.replace(/^\.?\//, "");
            absoluteUrl = backendOrigin ? `${backendOrigin}/${normalizedPath}` : normalizedPath;
        }

        const stamp = profile?.updatedAt || profile?._id || "";
        if (!stamp) return absoluteUrl;
        return absoluteUrl.includes("?")
            ? `${absoluteUrl}&v=${encodeURIComponent(String(stamp))}`
            : `${absoluteUrl}?v=${encodeURIComponent(String(stamp))}`;
    }, [profiles]);

    const stripCompanyLogoPlaceholderFromHtml = useCallback((html) => {
        if (!html || typeof html !== "string") return html;
        try {
            const doc = new DOMParser().parseFromString(html, "text/html");
            const walker = doc.createTreeWalker(doc.body, NodeFilter.SHOW_TEXT);
            const toRemove = [];
            while (walker.nextNode()) {
                const n = walker.currentNode;
                const t = String(n?.nodeValue || "").trim();
                if (t === "Company Logo") toRemove.push(n);
            }
            toRemove.forEach((n) => n.parentNode?.removeChild(n));

            Array.from(doc.querySelectorAll("img")).forEach((img) => {
                const src = String(img.getAttribute("src") || "").trim();
                const alt = String(img.getAttribute("alt") || "").trim().toLowerCase();
                if (!src && alt.includes("company")) img.remove();
                if (src === "undefined" || src === "null") img.remove();
            });

            return doc.body.innerHTML;
        } catch {
            return html.replace(/Company Logo/g, "");
        }
    }, []);

    /**
     * CRM master templates / QA drafts often contain accidental inline data-URI images from local testing.
     * Strip those only when loading fresh template from URL — not when generating (user may add images in-session).
     */
    const stripCrmPersistedInlineImagesFromQuotationBody = useCallback((html) => {
        if (!html || typeof html !== "string") return html;
        try {
            const doc = new DOMParser().parseFromString(html, "text/html");
            doc.querySelectorAll("img[src]").forEach((img) => {
                const src = String(img.getAttribute("src") || "").trim();
                const alt = String(img.getAttribute("alt") || "").trim().toLowerCase();
                if (/^data:image/i.test(src) || alt === "uploaded image") {
                    img.remove();
                }
            });
            return doc.body.innerHTML;
        } catch {
            return html.replace(/<img[^>]*\ssrc=["']data:image[^"']*["'][^>]*>/gi, "");
        }
    }, []);

    const CRM_HEADER_MARKER = "<!-- CRM_QUOTATION_HEADER -->";
    const CRM_HEADER_EMAIL = "info@indusinformatics.com";

    const buildQuotationHeaderHtml = useCallback((logoUrl) => {
        const safeLogo = String(logoUrl || "").trim();
        return `
${CRM_HEADER_MARKER}
<div class="quotation-header" style="width:100%; display:flex; align-items:flex-start; justify-content:space-between; margin-top:6px;">
  <div style="flex:0 0 45%; display:flex; align-items:center;">
    ${
            safeLogo
                ? `<img src="${safeLogo.replace(/"/g, "&quot;")}" alt="Company Logo" style="max-height:60px;width:auto;object-fit:contain;display:block;" onerror="this.insertAdjacentHTML('afterend','<span>Company Logo</span>');this.remove();" />`
                : `<span>Company Logo</span>`
        }
  </div>
  <div style="flex:0 0 auto; width:1px; background:#000; height:60px; margin:0 14px;"></div>
  <div style="flex:1; text-align:right; font-size:12px; line-height:1.4; padding-top:10px;">
    <div><strong>Email:</strong> info@indusinformatics.com</div>
    <div><strong>Address:</strong> 4830, Osage Avenue,<br/>Philadelphia, PA 19143</div>
  </div>
</div>
<div style="margin-top:10px; border-top:2px solid #000;"></div>
`;
    }, []);

    const stripExistingCrmQuotationHeader = useCallback((html) => {
        if (!html || typeof html !== "string") return html;
        const raw = String(html);

        if (!raw.includes(CRM_HEADER_MARKER) && !raw.toLowerCase().includes(CRM_HEADER_EMAIL)) {
            return raw;
        }

        try {
            const doc = new DOMParser().parseFromString(raw, "text/html");

            const markerNode = (() => {
                const walker = doc.createTreeWalker(
                    doc.body,
                    NodeFilter.SHOW_COMMENT,
                    null
                );
                let n = walker.nextNode();
                while (n) {
                    if (
                        n.nodeType === Node.COMMENT_NODE &&
                        String(n.nodeValue || "").trim() === "CRM_QUOTATION_HEADER"
                    ) {
                        return n;
                    }
                    n = walker.nextNode();
                }
                return null;
            })();

            if (markerNode) {
                let current = markerNode.nextSibling;
                markerNode.parentNode?.removeChild(markerNode);

                while (current && current.nodeType === Node.TEXT_NODE && !String(current.textContent || "").trim()) {
                    const next = current.nextSibling;
                    current.parentNode?.removeChild(current);
                    current = next;
                }

                if (current && current.nodeType === Node.ELEMENT_NODE) {
                    const el = current;
                    const isHeader = el.classList?.contains("quotation-header");
                    if (isHeader) {
                        const headerNext = el.nextSibling;
                        el.parentNode?.removeChild(el);

                        if (headerNext && headerNext.nodeType === Node.ELEMENT_NODE) {
                            const divEl = headerNext;
                            const style = String(divEl.getAttribute("style") || "")
                                .replace(/\s+/g, " ")
                                .toLowerCase();
                            if (style.includes("border-top") && style.includes("2px")) {
                                divEl.parentNode?.removeChild(divEl);
                            }
                        }
                    }
                }

                return doc.body.innerHTML;
            }

            // Fallback: remove header by email match (for legacy saved quotes without marker).
            const emailNode = Array.from(doc.querySelectorAll("div")).find((d) =>
                String(d.textContent || "").toLowerCase().includes(CRM_HEADER_EMAIL)
            );
            if (emailNode) {
                const headerRoot = emailNode.closest(".quotation-header") || emailNode.closest("div");
                if (headerRoot) {
                    const headerNext = headerRoot.nextSibling;
                    headerRoot.parentNode?.removeChild(headerRoot);

                    if (headerNext && headerNext.nodeType === Node.ELEMENT_NODE) {
                        const divEl = headerNext;
                        const style = String(divEl.getAttribute("style") || "")
                            .replace(/\s+/g, " ")
                            .toLowerCase();
                        if (style.includes("border-top") && style.includes("2px")) {
                            divEl.parentNode?.removeChild(divEl);
                        }
                    }
                }
            }

            return doc.body.innerHTML;
        } catch {
            const idx = raw.indexOf(CRM_HEADER_MARKER);
            if (idx >= 0) return raw.slice(idx + CRM_HEADER_MARKER.length);
            return raw;
        }
    }, []);

    const sanitizeCrmEditorHtml = useCallback(
        (html) => {
            let out = html;
            out = stripCompanyLogoPlaceholderFromHtml(out || "");
            out = stripExistingCrmQuotationHeader(out || "");
            return out;
        },
        [stripCompanyLogoPlaceholderFromHtml, stripExistingCrmQuotationHeader]
    );

    const isCRM = useMemo(() => {
        const subscriptionPackage = String(profiles?.[0]?.subscriptionpackage || "")
            .trim()
            .toUpperCase();
        return subscriptionPackage === "CRM";
    }, [profiles]);

    useEffect(() => {
        const p = profiles?.[0];
        if (!p) return;
        console.log("Company Logo:", {
            logoPath: p?.logoPath,
            companylogo: p?.companylogo,
            companyLogo: p?.companyLogo,
            resolvedCompanyLogoUrl,
        });
    }, [profiles, resolvedCompanyLogoUrl]);

    // const debounceTimeoutRef = useRef(null);


    // Jodit Editor Ref
    const editor = useRef(null);
    const imageUploadInputRef = useRef(null);
    const activeEditorForImageRef = useRef(null);

    const isFinanceOrHardwareEditableInputName = useCallback((name) => {
        const n = String(name || "");
        return (
            n.startsWith("servicePrice_") ||
            n.startsWith("hardwarePartName_") ||
            n.startsWith("hardwarePartQty_")
        );
    }, []);

    const attachCrmFinanceHardwareEditorHandlers = useCallback(
        (jodit, root) => {
            attachCrmHardwareTableEditorHandlers({
                jodit,
                root,
                setContent,
                isFinanceOrHardwareEditableInputName,
            });
        },
        [isFinanceOrHardwareEditableInputName, setContent]
    );

    const prepareSelectionForImageInsert = useCallback((joditInstance) => {
        const j = joditInstance;
        if (!j?.selection?.save) return;
        try {
            if (j.selection.hasMarkers) {
                j.selection.restore();
            }
            j.selection.save();
        } catch {
            /* ignore */
        }
    }, []);

    const handlePickImageFromDevice = useCallback(
        (joditInstance) => {
            const j =
                joditInstance || editor.current?.editor || editor.current || null;
            activeEditorForImageRef.current = j;
            prepareSelectionForImageInsert(j);
            if (imageUploadInputRef.current) {
                imageUploadInputRef.current.value = "";
                imageUploadInputRef.current.click();
            }
        },
        [prepareSelectionForImageInsert]
    );

    const handleImageFileSelected = useCallback(
        (event) => {
            const file = event?.target?.files?.[0];
            if (!file) return;
            if (!file.type?.startsWith("image/")) {
                enqueueSnackbar("Please select a valid image file", { variant: "warning" });
                return;
            }
            const maxBytes = 8 * 1024 * 1024;
            if (file.size > maxBytes) {
                enqueueSnackbar("Image file is too large (max 8 MB). Choose a smaller file.", {
                    variant: "warning",
                });
                return;
            }

            const objectUrl = URL.createObjectURL(file);
            const img = new Image();
            img.onload = () => {
                try {
                    // Template editor: small centered images (match CRM P&L-style quotation, ~200px wide).
                    const maxW = 200;
                    const maxH = 600;
                    let w = img.naturalWidth || img.width;
                    let h = img.naturalHeight || img.height;
                    if (!w || !h) {
                        enqueueSnackbar("Unable to read image dimensions.", { variant: "error" });
                        return;
                    }
                    const scale = Math.min(1, maxW / w, maxH / h);
                    const tw = Math.max(1, Math.round(w * scale));
                    const th = Math.max(1, Math.round(h * scale));

                    const canvas = document.createElement("canvas");
                    canvas.width = tw;
                    canvas.height = th;
                    const ctx = canvas.getContext("2d");
                    if (!ctx) {
                        enqueueSnackbar("Unable to process image.", { variant: "error" });
                        return;
                    }
                    ctx.fillStyle = "#ffffff";
                    ctx.fillRect(0, 0, tw, th);
                    ctx.drawImage(img, 0, 0, tw, th);

                    const compressedSrc = canvas.toDataURL("image/jpeg", 0.82);
                    const joditInstance =
                        activeEditorForImageRef.current ||
                        editor.current?.editor ||
                        editor.current ||
                        null;
                    if (!joditInstance?.selection) return;

                    const safeSrc = compressedSrc.replace(/"/g, "&quot;");
                    const imageHtml = `<img src="${safeSrc}" alt="Uploaded Image" style="display:block;margin:0 auto;max-width:200px;width:auto;height:auto;object-fit:contain;float:none;" />`;

                    try {
                        joditInstance.selection.focus({ preventScroll: true });
                        joditInstance.selection.restore();
                    } catch {
                        /* ignore */
                    }
                    try {
                        if (joditInstance.s?.insertHTML) {
                            joditInstance.s.insertHTML(imageHtml, true);
                        } else if (joditInstance.selection?.insertHTML) {
                            joditInstance.selection.insertHTML(imageHtml, true);
                        }
                    } catch {
                        /* ignore */
                    }
                } finally {
                    URL.revokeObjectURL(objectUrl);
                }
            };
            img.onerror = () => {
                URL.revokeObjectURL(objectUrl);
                enqueueSnackbar("Unable to process selected image", { variant: "error" });
            };
            img.src = objectUrl;
        },
        [enqueueSnackbar]
    );

    // Jodit configuration
    const config = useMemo(
        () => ({
            saveSelectionOnBlur: true,
            readonly: false,
            height: 400,
            toolbarAdaptive: false,
            removeButtons: ["source", "about"],
            events: {
                afterInit: (jodit) => {
                    try {
                        const root =
                            jodit?.container ||
                            jodit?.editor?.parentElement ||
                            null;
                        const toolbarRoot =
                            root?.querySelector?.(".jodit-toolbar__box") ||
                            root?.querySelector?.(".jodit-toolbar") ||
                            root?.querySelector?.(".jodit-toolbar-editor-collection") ||
                            null;
                        if (!toolbarRoot) return;

                        const onToolbarImagePointerDownCapture = (e) => {
                            const t = e.target;
                            if (!t || !t.closest) return;
                            const isImageBtn =
                                t.closest('[data-name="image"]') ||
                                t.closest(".jodit-toolbar-button_image") ||
                                t.closest(".jodit-toolbar-button__image") ||
                                t.closest('[aria-label*="image" i]') ||
                                t.closest('[title*="image" i]');
                            if (!isImageBtn) return;
                            e.preventDefault();
                            e.stopPropagation();
                            e.stopImmediatePropagation?.();
                            handlePickImageFromDevice(jodit);
                        };

                        toolbarRoot.addEventListener(
                            "mousedown",
                            onToolbarImagePointerDownCapture,
                            true
                        );
                        jodit.e?.one?.("beforeDestruct", () => {
                            toolbarRoot.removeEventListener(
                                "mousedown",
                                onToolbarImagePointerDownCapture,
                                true
                            );
                        });

                        const editorEl = jodit.editor;
                        if (editorEl) {
                            attachCrmFinanceHardwareEditorHandlers(jodit, editorEl);
                        }
                        const clearStaleImageMarkers = () => {
                            try {
                                if (
                                    isFocusedCrmQuotationEditableTableInput(
                                        editorEl,
                                        isFinanceOrHardwareEditableInputName
                                    )
                                ) {
                                    return;
                                }
                                if (!jodit.selection?.hasMarkers || !jodit.selection.isInsideArea) {
                                    return;
                                }
                                jodit.selection.restore();
                            } catch {
                                /* ignore */
                            }
                        };
                        if (editorEl) {
                            jodit.e.on(editorEl, "mouseup", clearStaleImageMarkers);
                            jodit.e.on(editorEl, "keyup", clearStaleImageMarkers);
                        }
                        jodit.e?.one?.("beforeDestruct", () => {
                            if (editorEl) {
                                jodit.e.off(editorEl, "mouseup", clearStaleImageMarkers);
                                jodit.e.off(editorEl, "keyup", clearStaleImageMarkers);
                            }
                        });

                        const patchImageUrlDialog = (dialogRoot) => {
                            if (!dialogRoot || dialogRoot.dataset?.pskImageDialogPatched) return;
                            const hasUrlInput =
                                dialogRoot.querySelector?.('input[name="url"], input[placeholder*="http" i]') ||
                                dialogRoot.querySelector?.('input[name="src"]');
                            const hasAlt =
                                dialogRoot.querySelector?.('input[name="alt"], input[placeholder*="Alternative" i]');
                            if (!hasUrlInput && !hasAlt) return;
                            dialogRoot.dataset.pskImageDialogPatched = "1";

                            dialogRoot
                                .querySelectorAll?.("input, label, textarea, .jodit-form__group, .jodit-form__fieldset")
                                .forEach((el) => {
                                    const tag = (el.tagName || "").toLowerCase();
                                    if (tag === "button") return;
                                    el.style.display = "none";
                                });

                            const urlClickable =
                                dialogRoot.querySelector?.('.jodit-tabs__button[role="tab"]') ||
                                Array.from(dialogRoot.querySelectorAll?.("button, div, span") || []).find(
                                    (el) =>
                                        String(el.textContent || "").trim().toLowerCase() === "url"
                                );
                            if (urlClickable) {
                                urlClickable.style.cursor = "pointer";
                                urlClickable.style.userSelect = "none";
                                urlClickable.style.pointerEvents = "auto";
                                urlClickable.addEventListener(
                                    "click",
                                    (e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        e.stopImmediatePropagation?.();
                                        handlePickImageFromDevice(jodit);
                                    },
                                    true
                                );
                            }
                        };

                        const observer = new MutationObserver(() => {
                            const candidates = Array.from(
                                document.querySelectorAll(".jodit-dialog, .jodit-popup, .jodit-dialog__box")
                            );
                            candidates.forEach((c) => patchImageUrlDialog(c));
                        });
                        observer.observe(document.body, { childList: true, subtree: true });
                        jodit.e?.one?.("beforeDestruct", () => {
                            observer.disconnect();
                        });
                    } catch {
                        // ignore
                    }
                },
            },
            buttons: [
                "bold",
                "italic",
                "underline",
                "strike",
                "|",
                "ul",
                "ol",
                "|",
                "font",
                "fontsize",
                "brush",
                "|",
                "table",
                "|",
                "link",
                "image",
                "|",
                "align",
                "|",
                "undo",
                "redo",
                "|",
                "hr",
                "|",
                "fullsize",
            ],
            table: {
                allowCellSelection: true,
                allowResize: true,
            },
            pasteHTMLAction: "insert_clear_html", // Important for preserving HTML
        }),
        [
            handlePickImageFromDevice,
            attachCrmFinanceHardwareEditorHandlers,
            isFinanceOrHardwareEditableInputName,
        ]
    );


    // const handleAutosaveQuote = useCallback(
    //     async (content) => {
    //         const data = {
    //             _id: autosaveid,
    //             type: "AutoSave",
    //             leadid: leadid,
    //             templateid: Templateid,
    //             templatedata: content,
    //             jsondata: JSON.stringify(replacedData),
    //         };
    //         try {
    //             await dispatch(editGenerateQuotation(data));
    //         } catch (error) {
    //             console.error("Failed to create quotation template:", error);
    //             const errorMessage =
    //                 error?.error ||
    //                 error?.message ||
    //                 "Something went wrong. Please try again.";
    //             enqueueSnackbar(errorMessage, {
    //                 variant: "error",
    //                 style: { zIndex: 10000 },
    //             });
    //         } finally {
    //             dispatch(fetchAllQuotations());
    //             dispatch(fetchAllQuotationTemplates());
    //         }
    //     },
    //     [Templateid, leadid, autosaveid, replacedData, enqueueSnackbar, dispatch]
    // );

    // Handle editor change with proper debounce
    // const handleEditorChangeWithDebounce = useCallback(
    //     (newContent) => {
    //         setContent(newContent);

    //         // Clear any existing timeout
    //         if (debounceTimeoutRef.current) {
    //             clearTimeout(debounceTimeoutRef.current);
    //         }
    //         debounceTimeoutRef.current = setTimeout(() => {
    //             handleAutosaveQuote(newContent);
    //         }, 2000); // 2000ms = 2 seconds
    //     },
    //     [handleAutosaveQuote]
    // );

    // Cleanup timeout on unmount
    // useEffect(() => {
    //     return () => {
    //         if (debounceTimeoutRef.current) {
    //             clearTimeout(debounceTimeoutRef.current);
    //         }
    //     };
    // }, []);

    // Load latest auto-save - UPDATE THIS FOR JODIT

    // useEffect(() => {
    //     if (quotations && quotations.length > 0) {
    //         const filteredQuotations = quotations.filter((q) => q.leadid === leadid);
    //         const autoSaves = filteredQuotations.filter((q) => q.type === "AutoSave");
    //         const sorted = autoSaves.sort(
    //             (a, b) => new Date(b.createdAt) - new Date(a.createdAt)
    //         );
    //         const latestAutoSave = sorted[0];
    //         if (latestAutoSave?.templatedata) {
    //             setContent(latestAutoSave.templatedata); // Use setContent instead of setValue
    //         }
    //         setautosaveid(latestAutoSave?._id);
    //         setTemplateid(latestAutoSave?.templateid);
    //     }
    // }, [quotations, leadid]);

    const fetchFiles = useCallback(async () => {
        if (!fileid) {
            return;
        }
        try {
            const files = await dispatch(getFilesByLead(fileid)).unwrap();
            const pdfFiles = files.filter((file) =>
                file.filename?.toLowerCase().endsWith(".pdf")
            );
            seshowfile(pdfFiles);
            const fileNames = pdfFiles.map((file) => file.filename);
            sefileName(fileNames);
        } catch (err) {
            console.error("Error fetching files:", err);
        }
    }, [dispatch, fileid]);

    useEffect(() => {
        fetchFiles();
    }, [fetchFiles]);

    // Filter quotations by quotation ID first (from lead), then fall back to leadid
    useEffect(() => {
        if (!quotations?.length) {
            sequotations([]);
            return;
        }

        // Get quotation ID from createdProject if available
        const leadQuotationId = createdProject?.[0]?.quotationId;
        const hasQuotationId = leadQuotationId != null && String(leadQuotationId).trim() !== "";

        let filteredQuotations = [];

        if (hasQuotationId) {
            // Filter by quotationId first (mandatory when available)
            // Match quotations that have the same quotationId in their lead reference
            filteredQuotations = quotations.filter((q) => {
                // Check if quotation has quotationId field
                if (q.quotationId != null && String(q.quotationId).trim() === String(leadQuotationId).trim()) {
                    return true;
                }
                // Check if quotation's leadid matches a lead with this quotationId
                const relatedLead = leads?.find((lead) => 
                    (lead.id === q.leadid || lead._id === q.leadid) &&
                    lead.quotationId != null &&
                    String(lead.quotationId).trim() === String(leadQuotationId).trim()
                );
                return relatedLead != null;
            });
        } else if (leadid) {
            // Fallback to leadid filtering if quotationId is not available
            filteredQuotations = quotations.filter((q) => q.leadid === leadid);
        }

        sequotations(filteredQuotations);
    }, [quotations, leads, leadid, createdProject]);

    // Set lead data based on quotation ID first, then fall back to project name
    useEffect(() => {
        // Get quotation ID from createdProject if available (passed from P&L wizard)
        const leadQuotationId = createdProject?.[0]?.quotationId;
        const hasQuotationId = leadQuotationId != null && String(leadQuotationId).trim() !== "";

        let matchedLead = null;

        if (hasQuotationId && leads?.length > 0) {
            // Match by quotationId first (mandatory when available)
            matchedLead = leads.find((lead) => 
                lead.quotationId != null && String(lead.quotationId).trim() === String(leadQuotationId).trim()
            );
        }

        // Fallback to ProjectName matching if quotationId is not available or no match found
        if (!matchedLead && leads?.length > 0 && selectedProject) {
            const filteredLeads = leads.filter((lead) =>
                lead?.ProjectName?.toLowerCase()?.includes(selectedProject?.toLowerCase())
            );
            matchedLead = filteredLeads[0] || null;
        }

        if (matchedLead) {
            setfileid(matchedLead._id);
            setleadid(matchedLead.id);
            sethistoryid(matchedLead.id);
            setSelectedLead(matchedLead);
        }
    }, [leads, selectedProject, createdProject]);

    // Fetch initial data
    useEffect(() => {
        dispatch(fetchAllQuotations());
        dispatch(fetchAllQuotationTemplates());
        dispatch(fetchCompanyProfiles());
        dispatch(fetchLeads());
        dispatch(fetchAllProjects());
        dispatch(fetchOtherExpenses());
    }, [dispatch]);

    // Set templates
    useEffect(() => {
        settemplate(templates);
    }, [templates]);

    // Click outside handlers
    const groupdownRef = useRef(null);
    const handleClickOutsidegroup = (e) => {
        if (groupdownRef.current && !groupdownRef.current.contains(e.target)) {
            setIsTemplateOpen(false);
        }
    };

    useEffect(() => {
        document.addEventListener("click", handleClickOutsidegroup);
        return () => {
            document.removeEventListener("click", handleClickOutsidegroup);
        };
    }, []);

    const versionRef = useRef(null);
    const handleClickOutsideversion = useCallback(
        (e) => {
            if (versionRef.current && !versionRef.current.contains(e.target)) {
                setisHistoryOpen(false);
            }
        },
        [versionRef]
    );

    useEffect(() => {
        document.addEventListener("click", handleClickOutsideversion);
        return () => {
            document.removeEventListener("click", handleClickOutsideversion);
        };
    }, [handleClickOutsideversion]);

    // Replace placeholders in template - UPDATE FOR JODIT
    const replacePlaceholders = (html, lead, profile) => {
        if (!lead) return { processedHtml: html, replacedData: {} };

        const currentDate = new Date().toLocaleDateString();
        const subscriptionPackage = String(profile?.subscriptionpackage || '').trim().toUpperCase();
        const isCRMOrERP = ['CRM', 'ERP'].includes(subscriptionPackage);

        const replacedData = {
            "Project Name": lead.ProjectName || "",
            "Client Name": lead.ClientName || "",
            "Current Date": currentDate,
            "Client Contact Name": lead.ClientContactName || "",
            "Client Contact Email": lead.ClientEmail || "",
            "Company Profile Name": profile?.companyname || "",
            // CRM: logo is only from buildQuotationHeaderHtml(resolvedCompanyLogoUrl); avoid duplicate/stale companylogo in body
            "Company Profile Logo":
                subscriptionPackage === "CRM" ? "" : profile?.companylogo || "",
            "Company Profile Email": profile?.companyemail || "",
            "Company Profile Address": profile?.companyaddress || "",
            "File Names": fileName || "",
            templateId: Templateid,
        };

        if (isCRMOrERP) {
            replacedData["Structural Monitoring Proposal"] =
                "Structural Monitoring Proposal for Bridge Infrastructure<br/>" +
                "To implement a reliable and scalable structural monitoring solution that provides continuous monitoring through high-precision accelerometers and visual inspection using intelligent camera systems.<br/>" +
                "<br/>" +
                "1. Solution Overview<br/>" +
                "Our STRUCTURAL MONITORING system integrates sensor-based vibration monitoring with visual inspection tools to provide comprehensive insights into structural performance. The system includes:<br/>" +
                "<br/>" +
                "• <strong>Accelerometer Modules:</strong> For real-time measurement of vibrations, tilt, and dynamic structural responses.<br/>" +
                "• <strong>Camera Modules:</strong> For periodic or event-triggered visual capture of the structure, enabling visual correlation with sensor data.<br/>" +
                "• <strong>Centralized Monitoring Interface:</strong> Displays synchronized data, alerts, and reports to the end-user.<br/>" +
                "<br/>" +
                "2. Key Features<br/>" +
                "• <strong>Time-Synchronized Data Acquisition:</strong> Sensor and camera data aligned to a timeframe.<br/>" +
                "• <strong>Remote Monitoring Dashboard:</strong> Real-time access to data, event logs, and visual content.<br/>" +
                "• <strong>Threshold Alerts:</strong> Notifications triggered by abnormal vibration or tilt events.";

            replacedData["Project Background"] =
                "Webber has requested structural monitoring service for their Mid Toll Bridge, Destin Bridge and Brookes bridge located at Destin, Florida.<br/>" +
                "<br/>" +
                "To address this, Indus Informatics will implement a structural monitoring solution designed to provide real-time notifications of any structural response events using a combination of precision accelerometer and tilt sensor technologies. In the event of significant structural activity or displacement, Indus Informatics will issue automated alerts to designated WEBBER personnel via SMS, email and voice call.<br/>" +
                "<br/>" +
                "The Indus Informatics Structural Monitoring system consists of three core components:<br/>" +
                "1. <strong>Sensor Units</strong> &ndash; Capture and monitor dynamic and static structural behaviour (vibration, tilt, and temperature).<br/>" +
                "2. <strong>Communications &amp; Power Hub</strong> &ndash; Controls the system's operations and transmits data.<br/>" +
                "3. <strong>Cloud-based Monitoring Platform</strong> &ndash; Receives and processes all incoming data for real-time analysis and alert generation.";
        }

        let processedHtml = html;

        // Replace regular placeholders
        Object.entries(replacedData).forEach(([key, value]) => {
            const regex = new RegExp(`{{${key}}}`, "g");
            processedHtml = processedHtml.replace(regex, value);
        });

        // Check for Summary Tables placeholder
        const hasSummaryTables = html.includes("{{Summary Tables}}");

        if (hasSummaryTables) {
            processedHtml = processedHtml.replace(
                /{{Summary Tables}}/g,
                "<!-- SUMMARY_TABLES -->"
            );
        }

        // Clean up any leftover placeholders
        processedHtml = processedHtml.replace(/{{[^}]+}}/g, "");

        // ERP: keep logo header inside the template.
        // CRM: do NOT inject logo into the editor HTML; we'll render it outside editor and prepend during Generate.
        if (isCRMOrERP) {
            if (subscriptionPackage === "ERP") {
                processedHtml = buildQuotationHeaderHtml(resolvedCompanyLogoUrl) + processedHtml;
            } else {
                processedHtml = stripCompanyLogoPlaceholderFromHtml(processedHtml);
            }
        }

        return { processedHtml, replacedData };
    };

    // Load template - UPDATE FOR JODIT
    const loadTemplate = async (tplUrl) => {
        try {
            const res = await fetch(tplUrl);
            if (!res.ok) throw new Error("Failed to fetch template");

            const html = await res.text();
            const parser = new DOMParser();
            const doc = parser.parseFromString(html, "text/html");
            let templateContent = doc.body.innerHTML;

            const profile = profiles.length > 0 ? profiles[0] : null;
            const { processedHtml, replacedData } = replacePlaceholders(
                templateContent,
                selectedLead,
                profile
            );

            // Now insert the dynamic HTML content where placeholders were
            let finalHtml = processedHtml;

            // Replace SUMMARY_TABLES placeholder if it exists and we have the HTML
            if (
                processedHtml.includes("<!-- SUMMARY_TABLES -->") &&
                serviceTableHTML
            ) {
                finalHtml = finalHtml.replace(
                    "<!-- SUMMARY_TABLES -->",
                    serviceTableHTML
                );
            }

            const subscriptionPackage = String(profile?.subscriptionpackage || "").trim().toUpperCase();
            if (subscriptionPackage === "CRM") {
                finalHtml = stripCompanyLogoPlaceholderFromHtml(finalHtml);
                finalHtml = stripCrmPersistedInlineImagesFromQuotationBody(finalHtml);
            }

            setContent(finalHtml); // Use setContent instead of setValue
            setReplacedData(replacedData);
        } catch (error) {
            console.error("Error loading template:", error);
            if (enqueueSnackbar) {
                enqueueSnackbar("Failed to load template", { variant: "error" });
            }
        }
    };

    // const formatDate = (dateString) => {
    //     const date = new Date(dateString);
    //     const month = String(date.getMonth() + 1).padStart(2, "0");
    //     const day = String(date.getDate()).padStart(2, "0");
    //     const year = date.getFullYear();
    //     return `${month}-${day}-${year}`;
    // };

    // const cleanNumericValues = (htmlContent) => {
    //     return htmlContent.replace(/>\s*([\d,]+)\s*</g, (match, numberWithCommas) => {
    //         const cleanNumber = numberWithCommas.replace(/,/g, '');
    //         return `>${cleanNumber}<`;
    //     });
    // };

    const cleanNumericValues = (htmlContent) => {
        return htmlContent;
        // return htmlContent.replace(
        //   />\s*([\d,]+)\s*</g,
        //   (match, numberWithCommas) => {
        //     const cleanNumber = numberWithCommas.replace(/,/g, "");
        //     return `>${cleanNumber}<`;
        //   }
        // );
    };

    const normalizeHardwareDetailsForGeneratedQuote = useCallback(
        (htmlContent) => {
            if (!htmlContent || typeof htmlContent !== "string") return htmlContent;
            try {
                const parser = new DOMParser();
                const doc = parser.parseFromString(htmlContent, "text/html");
                const headingNodes = Array.from(doc.querySelectorAll("p"));

                headingNodes.forEach((pEl) => {
                    const title = (pEl.textContent || "").trim().toLowerCase();
                    if (title !== "hardware details") return;

                    const sectionWrapper = pEl.parentElement;
                    if (!sectionWrapper) return;

                    const tableEl = sectionWrapper.querySelector("table");
                    if (tableEl) {
                        const rows = Array.from(tableEl.querySelectorAll("tbody tr"));
                        rows.forEach((tr) => {
                            const partInput = tr.querySelector(
                                'input[name^="hardwarePartName_"]'
                            );
                            const qtyInput = tr.querySelector(
                                'input[name^="hardwarePartQty_"]'
                            );
                            if (!partInput && !qtyInput) return;

                            const partVal = String(
                                partInput?.getAttribute("value") ?? partInput?.value ?? ""
                            ).trim();
                            const qtyVal = String(
                                qtyInput?.getAttribute("value") ?? qtyInput?.value ?? ""
                            ).trim();

                            if (partInput) {
                                partInput.parentElement.innerHTML =
                                    partVal || "&nbsp;";
                            }
                            if (qtyInput) {
                                qtyInput.parentElement.innerHTML = qtyVal || "&nbsp;";
                            }
                        });
                    }

                    // Remove add/remove row controls in generated quotation output.
                    const actionButtons = Array.from(
                        sectionWrapper.querySelectorAll("button")
                    );
                    actionButtons.forEach((btn) => btn.remove());
                });

                return doc.body.innerHTML;
            } catch (e) {
                console.warn("Failed to normalize hardware details table:", e);
                return htmlContent;
            }
        },
        []
    );

    const normalizeImageSourcesForGeneratedQuote = useCallback(
        (htmlContent) => {
            if (!htmlContent || typeof htmlContent !== "string") return htmlContent;
            try {
                const parser = new DOMParser();
                const doc = parser.parseFromString(htmlContent, "text/html");
                const backendUrl = String(configApi?.backendUrl || "").trim();
                const backendOrigin = backendUrl
                    .replace(/\/api\/?$/i, "")
                    .replace(/\/$/, "");

                const imgNodes = Array.from(doc.querySelectorAll("img[src]"));
                imgNodes.forEach((img) => {
                    const rawSrc = String(img.getAttribute("src") || "").trim();
                    if (!rawSrc) return;

                    const isAbsolute =
                        /^https?:\/\//i.test(rawSrc) ||
                        /^data:/i.test(rawSrc) ||
                        /^blob:/i.test(rawSrc);

                    if (!isAbsolute) {
                        const normalizedPath = rawSrc.replace(/^\.?\//, "");
                        const absoluteSrc = backendOrigin
                            ? `${backendOrigin}/${normalizedPath}`
                            : normalizedPath;
                        img.setAttribute("src", absoluteSrc);
                    }
                });

                return doc.body.innerHTML;
            } catch (e) {
                console.warn("Failed to normalize image sources:", e);
                return htmlContent;
            }
        },
        []
    );

    const loadlatedQuote = () => {
        const filteredQuotations = quotations.filter((q) => q.leadid === leadid);
        setFiles(filteredQuotations[0]?.generateddatafilepath);
        setsendid(filteredQuotations[0]?.leadid);
        setdownloadid(filteredQuotations[0]?._id);
        seteditContent(filteredQuotations[0]?.templatedata);
        setTemplateid(filteredQuotations[0]?.templateid);
    };

    const handleGenerate = async (contentOverride) => {
        if (!Templateid) {
            enqueueSnackbar("Please select template first", {
                variant: "error",
            });
            return;
        }

        const subscriptionPackage = String(
            profiles?.[0]?.subscriptionpackage || ""
        )
            .trim()
            .toUpperCase();
        const isCRM = subscriptionPackage === "CRM";
        const isERP = subscriptionPackage === "ERP";
        const isCRMOrERP = isCRM || isERP;

        const hasOverride = typeof contentOverride === "string" && contentOverride.length > 0;
        let finalContent = hasOverride ? contentOverride : content;
        if (isCRMOrERP) {
            // Preview mode: when override exists, do not touch editor refs/DOM.
            if (hasOverride) {
                // no-op
            } else {
                const editorInstance = editor.current?.editor || editor.current;

                // Prefer latest live editor HTML so newly inserted images are included.
                const liveEditorHtml =
                    (typeof editorInstance?.value === "string" && editorInstance.value) ||
                    (typeof editorInstance?.getEditorValue === "function" &&
                        editorInstance.getEditorValue()) ||
                    "";
                if (liveEditorHtml) {
                    finalContent = liveEditorHtml;
                }

                if (isCRM) {
                // Patch typed values into value="..." attributes (backend-readiness).
                try {
                    let editorDoc = null;
                    const iframeCandidates = [];
                    if (editorInstance?.iframe)
                        iframeCandidates.push(editorInstance.iframe);

                    const rootEl =
                        editorInstance?.container ||
                        editorInstance?.element ||
                        editorInstance?.el ||
                        editor.current;

                    if (rootEl?.querySelectorAll) {
                        iframeCandidates.push(
                            ...Array.from(rootEl.querySelectorAll("iframe"))
                        );
                    }

                    for (const iframeEl of iframeCandidates) {
                        const d =
                            iframeEl?.contentDocument ||
                            iframeEl?.contentWindow?.document ||
                            null;
                        if (d && d.querySelectorAll) {
                            editorDoc = d;
                            break;
                        }
                    }

                    if (!editorDoc) {
                        editorDoc =
                            editorInstance?.ownerDocument ||
                            editor.current?.ownerDocument ||
                            document;
                    }

                    const priceInputs = editorDoc?.querySelectorAll
                        ? editorDoc.querySelectorAll(
                            'input[name^="servicePrice_"], input[name^="hardwarePartName_"], input[name^="hardwarePartQty_"]'
                          )
                        : [];

                    if (priceInputs && priceInputs.length > 0) {
                        priceInputs.forEach((inputEl) => {
                            const nameAttr =
                                inputEl.getAttribute("name") || "";
                            if (!nameAttr) return;

                            const safeName = nameAttr.replace(
                                /[.*+?^${}()|[\]\\]/g,
                                "\\$&"
                            );

                            const rawVal = inputEl.value ?? "";
                            const escapedVal = String(rawVal)
                                .replace(/&/g, "&amp;")
                                .replace(/"/g, "&quot;");

                            const valueAttrRegex = new RegExp(
                                `(<input[^>]*name="${safeName}"[^>]*value=")[^"]*(")`,
                                "g"
                            );

                            const before = finalContent;
                            finalContent = finalContent.replace(
                                valueAttrRegex,
                                (m, p1, p2) => `${p1}${escapedVal}${p2}`
                            );

                            if (finalContent === before) {
                                const outer = inputEl.outerHTML;
                                const inputTagRegex = new RegExp(
                                    `<input[^>]*name="${safeName}"[^>]*>`,
                                    "g"
                                );
                                finalContent = finalContent.replace(
                                    inputTagRegex,
                                    outer
                                );
                            }
                        });
                    }
                } catch (e) {
                    console.warn(
                        "Failed to patch CRM service prices:",
                        e
                    );
                }

                finalContent = normalizeHardwareDetailsForGeneratedQuote(
                    finalContent
                );
                }
            }
        }

        // CRM: prepend logo header at generate time (decoupled from editor rendering)
        if (isCRM) {
            const headerHtml = buildQuotationHeaderHtml(resolvedCompanyLogoUrl);
            const bodyHtml = sanitizeCrmEditorHtml(finalContent || "");
            finalContent = `${headerHtml}${bodyHtml}`;
        }

        finalContent = normalizeImageSourcesForGeneratedQuote(finalContent);

        const cleanedContent = cleanNumericValues(finalContent);

        const tempId = Templateid;

        const data = {
            type: "GeneratedQuote",
            leadid: leadid,
            templateid: Templateid,
            templatedata: cleanedContent, // Use cleaned content
            jsondata: JSON.stringify(replacedData),
        };
        SetisLoading(true);
        try {
            await dispatch(createQuotation(data)).unwrap();
            setShowVersionHistoryHint(true);
            enqueueSnackbar("Quotation Created Successfully", {
                variant: "success",
                style: { zIndex: 10000 },
            });
        } catch (error) {
            console.error("Failed to create quotation template:", error);
            const errorMessage =
                error?.error ||
                error?.message ||
                "Something went wrong. Please try again.";
            enqueueSnackbar(errorMessage, {
                variant: "error",
                style: { zIndex: 10000 },
            });
        } finally {
            SetisLoading(false);
            dispatch(fetchAllQuotations());
            dispatch(fetchAllQuotationTemplates());
            setTemplateid(tempId);
            setIsEdit(false);
            loadlatedQuote()
        }
    };

    const handleedit = () => {
        setsendid(null);
        const safeBody = isCRM ? sanitizeCrmEditorHtml(editcontent || "") : (editcontent || "");
        setContent(safeBody);
        setEditedTemplate(safeBody);
        setIsEdit(true);
        setShowEditPreview(false);
    };

    // Save after edit: show preview window (do NOT generate yet)
    const handleSaveEdit = () => {
        const safeBody = isCRM ? sanitizeCrmEditorHtml(content || "") : (content || "");
        setEditedTemplate(safeBody);
        setShowEditPreview(true);
    };

    // Generate from edit preview: create new quotation version with edited content
    const handleGenerateFromPreview = async () => {
        const snapshot = editedTemplate || "";
        if (!snapshot) {
            console.error("No content available for generation");
            enqueueSnackbar("No edited content to generate. Please edit and Save first.", {
                variant: "error",
                style: { zIndex: 10000 },
            });
            return;
        }

        const payload = {
            quotationId: createdProject?.[0]?.quotationId ?? selectedLead?.quotationId,
            leadId: leadid,
            templateId: Templateid,
            content: snapshot,
        };
        console.log("Generate Payload:", payload);

        await handleGenerate(snapshot);
        setShowEditPreview(false);
        setIsEdit(false);
    };

    const handleDownload = async () => {
        if (!downloadid) {
            enqueueSnackbar("Please select template before download", {
                variant: "error",
                style: { zIndex: 10000 },
            });
            return;
        }

        SetisLoading(true);
        try {
            const response = await dispatch(downloadQuotationFile(downloadid));
            const resData = response?.payload;

            if (!resData?.downloadUrl) {
                throw new Error("Download failed");
            }

            const link = document.createElement("a");
            link.href = resData.downloadUrl;

            const extension = resData.contentType?.includes("pdf") ? "pdf" : "file";

            link.setAttribute("download", `quotation.${extension}`);
            document.body.appendChild(link);
            link.click();
            link.remove();

            enqueueSnackbar("Quotation File Successfully Downloaded", {
                variant: "success",
                style: { zIndex: 10000 },
            });
        } catch (error) {
            enqueueSnackbar(
                error?.message || "Something went wrong. Please try again.",
                {
                    variant: "error",
                    style: { zIndex: 10000 },
                }
            );
        } finally {
            SetisLoading(false);
            setdownloadid(null);
        }
    };

    const handleSend = async () => {
        if (!sendid) {
            enqueueSnackbar("Please select template first", {
                variant: "error",
            });
            return;
        }

        SetisLoading(true);
        try {
            await dispatch(
                sendQuotationToClient({
                    quotationId: setMailQuotation?._id,
                })
            );
            enqueueSnackbar("The quotation file has been sent successfully", {
                variant: "success",
                style: { zIndex: 10000 },
            });
        } catch (error) {
            console.error("Failed to create quotation template:", error);
            const errorMessage =
                error?.error ||
                error?.message ||
                "Something went wrong. Please try again.";
            enqueueSnackbar(errorMessage, {
                variant: "error",
                style: { zIndex: 10000 },
            });
        } finally {
            SetisLoading(false);
            setContent(""); // Use setContent instead of setValue
        }
    };

    const generateFinanceTable = useCallback(
        (projects) => {
            if (!projects || projects.length === 0) return "";
            const rows = projects
                .map((p) => {
                    return `
          <table style="width:60%; margin: 15px auto; border-collapse: collapse; font-size:13px;">
            
            <tr>
              <td style="border:1px solid #ccc; padding:8px; font-weight:bold; background-color: #eaeaea;">Project Name</td>
              <td style="border:1px solid #ccc; padding:8px; background-color: #eaeaea; ">${p.projectName}</td>
            </tr>

            <tr>
              <td style="border:1px solid #ccc; padding:8px; font-weight:bold;">Quoted Price(${currency})</td>
              <td style="border:1px solid #ccc; padding:8px;">${USAComma(p.QuotedValue)}</td>
            </tr>
          </table>
        `;
                })
                .join("");

            return `
      <div style="width: 100%; text-align:center; margin: 10px 0; font-weight:bold;">
        Finance Summary
      </div>

      ${rows}
    `;
        },
        [USAComma, currency]
    );

    const generateCrmFinanceAndHardwareTable = useCallback(() => {
        if (!selectedLead) return "";

        const cellStyle =
            "border: 1px solid #000; padding: 8px; min-width: 80px; min-height: 32px;";

        let tableHtml = "";

        const serviceTypesArrCandidate =
            selectedLead?.servicetypes ??
            selectedLead?.serviceTypes ??
            selectedLead?.ServiceTypes ??
            selectedLead?.TypeService ??
            [];

        const serviceTypes = (() => {
            const toTypeName = (st) => {
                if (!st) return "";
                if (typeof st === "string") return st;
                return (
                    st.TypeService ||
                    st.typeService ||
                    st.ServiceType ||
                    st.serviceType ||
                    st.type ||
                    ""
                );
            };

            if (Array.isArray(serviceTypesArrCandidate)) {
                return serviceTypesArrCandidate.map(toTypeName).filter(Boolean);
            }

            if (
                serviceTypesArrCandidate &&
                Array.isArray(serviceTypesArrCandidate.TypeService)
            ) {
                return serviceTypesArrCandidate.TypeService
                    .map(toTypeName)
                    .filter(Boolean);
            }

            if (
                serviceTypesArrCandidate &&
                Array.isArray(serviceTypesArrCandidate.serviceTypes)
            ) {
                return serviceTypesArrCandidate.serviceTypes
                    .map(toTypeName)
                    .filter(Boolean);
            }

            return [];
        })();

        const quotedTotal =
            selectedLead?.quotedprice ??
            selectedLead?.quotedPrice ??
            selectedLead?.QuotedPrice ??
            selectedLead?.QuotedValue ??
            0;

        const safeTotal = Number(quotedTotal) || 0;

        const isUSA = String(currency || "").trim() === "$";
        const onInputFormat = isUSA
            ? "let v=(this.value||'').toString().replace(/[^0-9]/g,''); this.value=v.replace(/\\B(?=(\\d{3})+(?!\\d))/g, ',');"
            : "let v=(this.value||'').toString().replace(/[^0-9]/g,''); if(v.length<=3){this.value=v;return;} let last3=v.slice(-3); let other=v.slice(0,-3); other=other.replace(/\\B(?=(\\d{2})+(?!\\d))/g, ','); this.value=other+','+last3;";

        const priceHeader = currency ? `Price (${currency})` : "Price";

        const hasHardwareService = serviceTypes.some(
            (st) => String(st || "").trim().toLowerCase() === "hardware"
        );
        const safeServiceTypes = serviceTypes.map((serviceType) =>
            String(serviceType).replace(/</g, "&lt;")
        );

        tableHtml = `<div style="margin: 12px 0; display: flex; justify-content: center;"><div style="width: 100%; max-width: 520px;"><p style="font-weight: bold; margin-bottom: 8px; text-align: center; font-size: 14px;">Finance Summary</p><table style="width: 100%; border-collapse: collapse; font-size: 12px; border: 1px solid #000; margin: 0 auto;"><thead><tr style="background-color: #e7ebfb;"><th style="${cellStyle} text-align: left;">Service types</th><th style="${cellStyle} text-align: right;">${priceHeader}</th></tr></thead><tbody>${safeServiceTypes
            .map((serviceType, idx) => {
                return `<tr style="background-color: #fff;"><td style="${cellStyle}">${serviceType}</td><td style="${cellStyle} text-align: right;"><input type="text" inputmode="numeric" style="width: 100%; border: none; outline: none; text-align: right; font-size: 12px;" name="servicePrice_${idx}" value="" onkeydown="event.stopPropagation(); event.stopImmediatePropagation && event.stopImmediatePropagation();" onkeypress="event.stopPropagation(); event.stopImmediatePropagation && event.stopImmediatePropagation();" onkeyup="event.stopPropagation(); event.stopImmediatePropagation && event.stopImmediatePropagation();" onbeforeinput="event.stopPropagation(); event.stopImmediatePropagation && event.stopImmediatePropagation();" onmousedown="event.stopPropagation();" onclick="event.stopPropagation();" oninput="${onInputFormat}; this.setAttribute('value', this.value);" /></td></tr>`;
            })
            .join("")}<tr style="background-color: #bfdbfe; font-weight: bold;"><td style="${cellStyle}">Total quoted price</td><td style="${cellStyle} text-align: right;">${USAComma(Number(safeTotal).toFixed(2))}</td></tr></tbody></table></div></div>`;

        if (hasHardwareService) {
            tableHtml += buildCrmHardwareDetailsSectionHtml(cellStyle);
        }
        /* legacy inline onclick hardware block removed - see buildCrmHardwareDetailsSectionHtml
        if (false && hasHardwareService) {
            tableHtml += `<div style="margin: 12px 0; display: flex; justify-content: center;"><div style="width: 100%; max-width: 520px;"><p style="font-weight: bold; margin-bottom: 8px; text-align: center; font-size: 14px;">Hardware Details</p><table style="width: 100%; border-collapse: collapse; font-size: 12px; border: 1px solid #000; margin: 0 auto;"><thead><tr style="background-color: #e7ebfb;"><th style="${cellStyle} text-align: left;">Part name</th><th style="${cellStyle} text-align: right;">Part qty</th></tr></thead><tbody><tr style="background-color: #fff;"><td style="${cellStyle}"><input type="text" style="width: 100%; border: none; outline: none; text-align: left; font-size: 12px;" name="hardwarePartName_0" value="" onkeydown="event.stopPropagation(); event.stopImmediatePropagation && event.stopImmediatePropagation();" onkeypress="event.stopPropagation(); event.stopImmediatePropagation && event.stopImmediatePropagation();" onkeyup="event.stopPropagation(); event.stopImmediatePropagation && event.stopImmediatePropagation();" onbeforeinput="event.stopPropagation(); event.stopImmediatePropagation && event.stopImmediatePropagation();" onmousedown="event.stopPropagation();" onclick="event.stopPropagation();" oninput="this.setAttribute('value', this.value);" /></td><td style="${cellStyle} text-align: right;"><input type="text" inputmode="numeric" style="width: 100%; border: none; outline: none; text-align: right; font-size: 12px;" name="hardwarePartQty_0" value="" onkeydown="event.stopPropagation(); event.stopImmediatePropagation && event.stopImmediatePropagation();" onkeypress="event.stopPropagation(); event.stopImmediatePropagation && event.stopImmediatePropagation();" onkeyup="event.stopPropagation(); event.stopImmediatePropagation && event.stopImmediatePropagation();" onbeforeinput="event.stopPropagation(); event.stopImmediatePropagation && event.stopImmediatePropagation();" onmousedown="event.stopPropagation();" onclick="event.stopPropagation();" oninput="this.value=(this.value||'').toString().replace(/[^0-9]/g,''); this.setAttribute('value', this.value);" /></td></tr></tbody></table><div style="display:flex; justify-content:flex-end; gap:8px; margin-top:8px;"><button type="button" style="padding:2px 10px; border:1px solid #000; background:#fff; cursor:pointer; font-size:12px;" onclick="(function(btn){var table=btn.closest('div').previousElementSibling; if(!table) return; var tbody=table.querySelector('tbody'); if(!tbody) return; var rowCount=tbody.querySelectorAll('tr').length; var uid=Date.now()+''+Math.floor(Math.random()*1000); var tr=document.createElement('tr'); tr.style.backgroundColor='#fff'; tr.innerHTML='<td style=&quot;${cellStyle}&quot;><input type=&quot;text&quot; style=&quot;width:100%; border:none; outline:none; text-align:left; font-size:12px;&quot; name=&quot;hardwarePartName_'+uid+'&quot; value=&quot;&quot; onkeydown=&quot;event.stopPropagation(); event.stopImmediatePropagation && event.stopImmediatePropagation();&quot; onkeypress=&quot;event.stopPropagation(); event.stopImmediatePropagation && event.stopImmediatePropagation();&quot; onkeyup=&quot;event.stopPropagation(); event.stopImmediatePropagation && event.stopImmediatePropagation();&quot; onbeforeinput=&quot;event.stopPropagation(); event.stopImmediatePropagation && event.stopImmediatePropagation();&quot; onmousedown=&quot;event.stopPropagation();&quot; onclick=&quot;event.stopPropagation();&quot; oninput=&quot;this.setAttribute(\\'value\\', this.value);&quot; /></td><td style=&quot;${cellStyle} text-align:right;&quot;><input type=&quot;text&quot; inputmode=&quot;numeric&quot; style=&quot;width:100%; border:none; outline:none; text-align:right; font-size:12px;&quot; name=&quot;hardwarePartQty_'+uid+'&quot; value=&quot;&quot; onkeydown=&quot;event.stopPropagation(); event.stopImmediatePropagation && event.stopImmediatePropagation();&quot; onkeypress=&quot;event.stopPropagation(); event.stopImmediatePropagation && event.stopImmediatePropagation();&quot; onkeyup=&quot;event.stopPropagation(); event.stopImmediatePropagation && event.stopImmediatePropagation();&quot; onbeforeinput=&quot;event.stopPropagation(); event.stopImmediatePropagation && event.stopImmediatePropagation();&quot; onmousedown=&quot;event.stopPropagation();&quot; onclick=&quot;event.stopPropagation();&quot; oninput=&quot;this.value=(this.value||\\'\\').toString().replace(/[^0-9]/g,\\'\\'); this.setAttribute(\\'value\\', this.value);&quot; /></td>'; tbody.appendChild(tr);var ni=tr.querySelector('input[name^=hardwarePartName_]');if(ni){setTimeout(function(){ni.focus();},0);}})(this)">+</button><button type="button" style="padding:2px 10px; border:1px solid #000; background:#fff; cursor:pointer; font-size:12px;" onclick="(function(btn){var table=btn.closest('div').previousElementSibling; if(!table) return; var tbody=table.querySelector('tbody'); if(!tbody) return; var rows=tbody.querySelectorAll('tr'); if(rows.length<=1) return; tbody.removeChild(rows[rows.length-1]);})(this)">-</button></motion></motion></motion>`;
        } */

        return tableHtml;
    }, [USAComma, currency, selectedLead]);

    const serviceTableHTML = useMemo(
        () => {
            const isCRM = profiles?.[0]?.subscriptionpackage === "CRM";
            if (isCRM) return generateCrmFinanceAndHardwareTable();
            return generateFinanceTable(calculatedFinanceProjects);
        },
        [
            calculatedFinanceProjects,
            generateFinanceTable,
            generateCrmFinanceAndHardwareTable,
            profiles,
        ]
    );


    return (
        <>
            {isLoading ? <AdminLoader isLoading={isLoading} /> : <></>}
            <input
                ref={imageUploadInputRef}
                type="file"
                accept="image/*"
                style={{ display: "none" }}
                onChange={handleImageFileSelected}
            />
            <div>

                <div className="fixed w-full inset-0 bg-black/50 flex items-center justify-center z-[1300]">
                    <div className="min-w-[60%] max-h-[98%] max-w-[70%] bg-white shadow-lg rounded-lg p-6">
                        <div className="flex justify-between items-center mb-6">
                            <h2 className="text-lg font-bold">Quotation</h2>

                            <button
                                onClick={() => {
                                    setIsEdit(false);
                                    setShowEditPreview(false);
                                    setShowQuoteModal(false);
                                }}
                                className="text-gray-500 hover:text-black"
                            >
                                <CloseIcon className=" cursor-pointer  bg-white" />
                            </button>
                        </div>
                        <div className="w-full flex justify-between">
                            <div className="flex gap-2">
                                <div
                                    className="relative flex flex-col gap-1 justify-center align-middle items-start"
                                    ref={groupdownRef}
                                >
                                    <label
                                        htmlFor="selectedStatus"
                                        className="block text-sm font-semibold text-gray-700"
                                    >
                                        Template
                                    </label>
                                    <button
                                        type="button"
                                        onClick={(e) => {
                                            e.preventDefault();
                                            setIsTemplateOpen(!isTemplateOpen);
                                        }}
                                        className="w-24 relative outline-none date-picker-placeholder flex justify-start border border-[#e0e7ff] rounded-md p-2 text-xs"
                                    >
                                        <span
                                            onClick={() => setAssignedGroup()}
                                            className="truncate min-w-0 flex-1 text-left"
                                        >
                                            {selectedTemplate ? selectedTemplate : "All"}
                                        </span>
                                        <ExpandMoreIcon className="absolute cursor-pointer right-1 top-1 bg-white" />
                                    </button>
                                    {isTemplateOpen && (
                                        <div className="absolute left-0 top-full p-1 w-full text-xs border overflow-hidden text-wrap border-gray-400 bg-white shadow-md rounded-md z-50">
                                            {/* <div
                                      className={`flex items-center p-2 hover:bg-gray-200 cursor-pointer ${AssignedGroup === "All" ? "bg-gray-300" : ""
                                        }`}
                                      onClick={() => {
                                        setIsTemplateOpen(false);
                                      }}
                                    >
                                      <label>All</label>
                                    </div> */}

                                            {template.map((temp) => (
                                                <div
                                                    key={temp.id}
                                                    className={`flex items-center p-2 hover:bg-gray-200 cursor-pointer `}
                                                    onClick={() => {
                                                        setIsTemplateOpen(false);
                                                        loadTemplate(temp.templatedataUrl);
                                                        setselectedTemplate(temp.templatename);
                                                        setTemplateid(temp._id);
                                                        setsendid("");
                                                        setFiles();
                                                    }}
                                                >
                                                    <label>{temp.templatename}</label>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                                <div
                                    ref={versionRef}
                                    className="relative flex flex-col gap-1 justify-center align-middle items-center"
                                >
                                    <label
                                        htmlFor="selectedStatus"
                                        className="block text-sm font-semibold text-gray-700"
                                    >
                                        Version History
                                    </label>
                                    <button
                                        type="button"
                                        onClick={(e) => {
                                            e.preventDefault();
                                            setShowVersionHistoryHint(false);
                                            setisHistoryOpen(!isHistoryOpen);
                                        }}
                                        className={`w-24 relative outline-none date-picker-placeholder flex justify-start rounded-md p-2 pr-7 text-xs transition-all duration-300 ${showVersionHistoryHint
                                            ? "border border-emerald-500 shadow-[0_0_0_3px_rgba(16,185,129,0.18)] animate-pulse"
                                            : "border border-[#e0e7ff]"
                                            }`}
                                    >
                                        <span
                                            // onClick={() => setAssignedGroup()}
                                            className="truncate min-w-0 flex-1 text-left"
                                        >
                                            All
                                        </span>
                                        {showVersionHistoryHint && (
                                            <span className="pointer-events-none absolute right-8 top-1/2 z-10 flex h-2.5 w-2.5 -translate-y-1/2">
                                                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-500 opacity-75" />
                                                <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-emerald-500" />
                                            </span>
                                        )}
                                        <ExpandMoreIcon className="absolute cursor-pointer right-1 top-1 bg-white" />
                                    </button>
                                    {isHistoryOpen && (
                                        <div className="absolute left-0 top-full text-xs border p-1 max-h-60 overflow-auto text-wrap border-gray-400 bg-white shadow-md rounded-md z-50">
                                            {(() => {
                                                // Filter only GeneratedQuote type quotations
                                                const generatedQuotes = quotation.filter(q => q.type === "GeneratedQuote");
                                                // Sort quotations by createdAt in ascending order (oldest first)
                                                const sortedQuotations = [...generatedQuotes].sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));
                                                
                                                return sortedQuotations.length > 0 ? (
                                                    sortedQuotations.map((quot, index) => (
                                                        <div
                                                            key={quot._id}
                                                            className="flex justify-between align-middle items-center p-2 hover:bg-gray-200 cursor-pointer"
                                                            onClick={() => {
                                                                setIsTemplateOpen(false);
                                                                setIsEdit(false);
                                                                setShowEditPreview(false);
                                                                if (quot.type === "GeneratedQuote") {
                                                                    setFiles(quot.generateddatafilepath);
                                                                    setsendid(quot.leadid);
                                                                    setdownloadid(quot._id);
                                                                    seteditContent(isCRM ? sanitizeCrmEditorHtml(quot.templatedata || "") : (quot.templatedata || ""));
                                                                    setTemplateid(quot.templateid);
                                                                    setsetMailQuotation(quot);
                                                                    try {
                                                                        setReplacedData(quot.jsondata ? JSON.parse(quot.jsondata) : {});
                                                                    } catch {
                                                                        setReplacedData({});
                                                                    }
                                                                } else {
                                                                    setFiles();
                                                                    setContent(quot.templatedata);
                                                                    setsendid(null);
                                                                    setsetMailQuotation(quot)
                                                                }
                                                                setisHistoryOpen(!isHistoryOpen);
                                                                setselectedTemplate("");
                                                            }}
                                                        >
                                                            <div className="flex items-center">
                                                                <span>{quot.quotationId ?? quot.QuotationId ?? quot.quotationNumber ?? quot.leadid ?? "—"}</span>
                                                                <span>_v{index + 1}</span>
                                                            </div>
                                                        </div>
                                                    ))
                                                ) : (
                                                    <div className="p-2 text-center text-gray-500">
                                                        No quotation
                                                    </div>
                                                );
                                            })()}
                                        </div>
                                    )}
                                </div>
                            </div>

                            <div className="flex gap-2 justify-end align-bottom items-end">
                                {showEditPreview ? (
                                    <>
                                        <button
                                            onClick={() => setShowEditPreview(false)}
                                            className="p-1.5 text-sm rounded-md bg-gray-500 text-white inline-block"
                                        >
                                            Back to Edit
                                        </button>
                                        <button
                                            onClick={handleGenerateFromPreview}
                                            className="p-1.5 text-sm rounded-md bg-[#e32278] text-white inline-block"
                                        >
                                            Generate
                                        </button>
                                    </>
                                ) : (
                                    <>
                                        <button
                                            onClick={IsEdit ? handleSaveEdit : handleGenerate}
                                            className="p-1.5 text-sm rounded-md bg-[#e32278] text-white inline-block"
                                        >
                                            {IsEdit ? "Save" : "Generate"}
                                        </button>
                                        {files && !IsEdit && (
                                            <button
                                                onClick={handleedit}
                                                className="px-3.5 py-1.5 text-sm rounded-md bg-[#e32278] text-white inline-block"
                                            >
                                                Edit
                                            </button>
                                        )}
                                        <button
                                            disabled={!files}
                                            onClick={() => handleDownload()}
                                            className={`${!files
                                                ? "cursor-not-allowed bg-gray-400"
                                                : "cursor-pointer bg-[#e32278]"
                                                } p-1.5 text-sm rounded-md text-white inline-block`}
                                        >
                                            Download
                                        </button>
                                        <button
                                            disabled={!files}
                                            onClick={handleSend}
                                            className={`${!files
                                                ? "cursor-not-allowed bg-gray-400"
                                                : "cursor-pointer bg-[#e32278]"
                                                } p-1.5 text-sm rounded-md text-white inline-block`}
                                        >
                                            Send Email
                                        </button>
                                    </>
                                )}
                            </div>
                        </div>
                        <div className="w-full min-h-[5rem] max-h-[24rem] overflow-y-scroll p-2 border mt-2">
                            {showEditPreview ? (
                                <div className="preview w-full p-4 bg-white">
                                    <div className="text-sm font-semibold text-gray-600 mb-2">Quotation Preview (Edited)</div>
                                    {isCRM && (
                                        <div style={{ marginBottom: 12 }}>
                                            <div style={{ width: "100%", display: "flex", alignItems: "flex-start", justifyContent: "space-between", marginTop: 6 }}>
                                                <div style={{ flex: "0 0 45%", display: "flex", alignItems: "center" }}>
                                                    {resolvedCompanyLogoUrl ? (
                                                        <img
                                                            src={resolvedCompanyLogoUrl}
                                                            alt="Company Logo"
                                                            style={{ maxHeight: 60, width: "auto", objectFit: "contain", display: "block" }}
                                                            onError={(e) => {
                                                                e.currentTarget.style.display = "none";
                                                            }}
                                                        />
                                                    ) : (
                                                        <span>Company Logo</span>
                                                    )}
                                                </div>
                                                <div style={{ flex: "0 0 auto", width: 1, background: "#000", height: 60, margin: "0 14px" }} />
                                                <div style={{ flex: 1, textAlign: "right", fontSize: 12, lineHeight: 1.4, paddingTop: 10 }}>
                                                    <div><strong>Email:</strong> info@indusinformatics.com</div>
                                                    <div><strong>Address:</strong> 4830, Osage Avenue,<br />Philadelphia, PA 19143</div>
                                                </div>
                                            </div>
                                            <div style={{ marginTop: 10, borderTop: "2px solid #000" }} />
                                        </div>
                                    )}
                                    <div
                                        className="border rounded p-4 min-h-[20rem] bg-white"
                                        dangerouslySetInnerHTML={{ __html: editedTemplate || content || "" }}
                                    />
                                </div>
                            ) : sendid ? (
                                <FileViewer files={files} />
                            ) : (
                                <div className="preview w-full shadow-[inset_0_0_10px_rgba(0,0,0,0.3)]">
                                    {content ? (
                                        <div className="flex h-[20rem]">
                                            <div className="w-full">
                                                {isCRM && (
                                                    <div style={{ padding: 12, background: "#fff" }}>
                                                        <div style={{ width: "100%", display: "flex", alignItems: "flex-start", justifyContent: "space-between", marginTop: 6 }}>
                                                            <div style={{ flex: "0 0 45%", display: "flex", alignItems: "center" }}>
                                                                {resolvedCompanyLogoUrl ? (
                                                                    <img
                                                                        src={resolvedCompanyLogoUrl}
                                                                        alt="Company Logo"
                                                                        style={{ maxHeight: 60, width: "auto", objectFit: "contain", display: "block" }}
                                                                        onError={(e) => {
                                                                            e.currentTarget.style.display = "none";
                                                                        }}
                                                                    />
                                                                ) : (
                                                                    <span>Company Logo</span>
                                                                )}
                                                            </div>
                                                            <div style={{ flex: "0 0 auto", width: 1, background: "#000", height: 60, margin: "0 14px" }} />
                                                            <div style={{ flex: 1, textAlign: "right", fontSize: 12, lineHeight: 1.4, paddingTop: 10 }}>
                                                                <div><strong>Email:</strong> info@indusinformatics.com</div>
                                                                <div><strong>Address:</strong> 4830, Osage Avenue,<br />Philadelphia, PA 19143</div>
                                                            </div>
                                                        </div>
                                                        <div style={{ marginTop: 10, borderTop: "2px solid #000" }} />
                                                    </div>
                                                )}
                                                <JoditEditor
                                                    ref={editor}
                                                    value={content}
                                                    config={config}
                                                    onChange={(newContent) => {
                                                        const editorRef =
                                                            editor.current?.editor ||
                                                            editor.current;
                                                        if (
                                                            isFocusedCrmQuotationEditableTableInput(
                                                                editorRef,
                                                                isFinanceOrHardwareEditableInputName
                                                            )
                                                        ) {
                                                            return;
                                                        }
                                                        const activeEl =
                                                            (typeof document !== "undefined" &&
                                                                document.activeElement) ||
                                                            null;
                                                        const activeName =
                                                            activeEl?.getAttribute?.(
                                                                "name"
                                                            ) || "";
                                                        if (
                                                            isFinanceOrHardwareEditableInputName(
                                                                activeName
                                                            )
                                                        ) {
                                                            return;
                                                        }
                                                        setContent(newContent);
                                                    }}
                                                />
                                            </div>
                                        </div>
                                    ) : (
                                        <div>No Template</div>
                                    )}

                                    {content &&
                                        (showfile?.[0]?.message ===
                                            "No files available for the given Lead ID" ||
                                            showfile?.files?.length === 0 ? (
                                            <div>No Attachament</div>
                                        ) : (
                                            <FilesViewer showfile={showfile} />
                                        ))}
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                {/* <div className="fixed w-full inset-0 bg-black/50 flex items-center justify-center z-[1300]">
                    <div className="min-w-[60%] max-h-[98%] max-w-[70%] bg-white shadow-lg rounded-lg p-6">
                        <div className="flex justify-between items-center mb-6">
                            <h2 className="text-lg font-bold">Quotation</h2>
                            <button
                                onClick={() => {
                                    setShowQuoteModal(false);
                                }}
                                className="text-gray-500 hover:text-black"
                            >
                                Close
                            </button>
                        </div>
                        <div className="w-full flex justify-between">
                            <div className="flex gap-2">
                                <div
                  className="relative flex flex-col gap-1 justify-center align-middle items-start"
                                    ref={groupdownRef}
                                >
                                    <label
                                        htmlFor="selectedStatus"
                                        className="block text-sm font-semibold text-gray-700"
                                    >
                                        Template
                                    </label>
                                    <button
                                        type="button"
                                        onClick={(e) => {
                                            e.preventDefault();
                                            setIsTemplateOpen(!isTemplateOpen);
                                        }}
                                        className="w-24 relative outline-none date-picker-placeholder flex justify-start border border-[#e0e7ff] rounded-md p-2 text-xs"
                                    >
                                        <span
                                            onClick={() => setAssignedGroup()}
                                            className="truncate min-w-0 flex-1 text-left"
                                        >
                                            {selectedTemplate ? selectedTemplate : "All"}
                                        </span>
                                        <ExpandMoreIcon className="absolute cursor-pointer right-1 top-1 bg-white" />
                                    </button>
                                    {isTemplateOpen && (
                                        <div className="absolute left-0 top-full w-full text-xs border overflow-hidden text-wrap border-gray-400 bg-white shadow-md rounded-md z-50">
                                            <div
                                                className={`flex items-center p-2 hover:bg-gray-200 cursor-pointer ${AssignedGroup === "All" ? "bg-gray-300" : ""
                                                    }`}
                                                onClick={() => {
                                                    setIsTemplateOpen(false);
                                                }}
                                            >
                                                <label>All</label>
                                            </div>

                                            {template.map((temp) => (
                                                <div
                                                    key={temp.id}
                                                    className={`flex items-center p-2 hover:bg-gray-200 cursor-pointer ${AssignedGroup === temp.newGroupName
                                                        ? "bg-gray-300"
                                                        : ""
                                                        }`}
                                                    onClick={() => {
                                                        setIsTemplateOpen(false);
                                                        loadTemplate(temp.templatedataUrl);
                                                        setselectedTemplate(temp.templatename);
                                                        setTemplateid(temp._id);
                                                        setsendid("");
                                                    }}
                                                >
                                                    <label>{temp.templatename}</label>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>

                                <div
                                    ref={versionRef}
                                    className="relative flex flex-col justify-center align-middle items-center"
                                >
                                    <label
                                        htmlFor="selectedStatus"
                                        className="block text-sm font-semibold text-gray-700 "
                                    >
                                        Version History
                                    </label>
                                    <button
                                        type="button"
                                        onClick={(e) => {
                                            e.preventDefault();
                                            setisHistoryOpen(!isHistoryOpen);
                                        }}
                                        className="w-24 relative outline-none date-picker-placeholder flex justify-start border border-[#e0e7ff] rounded-md p-2 text-xs"
                                    >
                                        <span
                                            onClick={() => setAssignedGroup()}
                                            className="truncate min-w-0 flex-1 text-left"
                                        >
                                            All
                                        </span>
                                        <ExpandMoreIcon className="absolute cursor-pointer right-1 top-1 bg-white" />
                                    </button>
                                           {isHistoryOpen && (
                    <div className="absolute left-0 top-full text-xs border p-1 max-h-60 overflow-auto text-wrap border-gray-400 bg-white shadow-md rounded-md z-50">
                      {(() => {
                        // Filter only GeneratedQuote type quotations
                        const generatedQuotes = quotation.filter(q => q.type === "GeneratedQuote");
                        // Sort quotations by createdAt in ascending order (oldest first)
                        const sortedQuotations = [...generatedQuotes].sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));
                        
                        return sortedQuotations.length > 0 ? (
                          sortedQuotations.map((quot, index) => (
                            <div
                              key={quot._id}
                              className="flex justify-between align-middle items-center p-2 hover:bg-gray-200 cursor-pointer"
                              onClick={() => {
                                setIsTemplateOpen(false);
                                if (quot.type === "GeneratedQuote") {
                                  setFiles(quot.generateddatafilepath);
                                  setsendid(quot.leadid);
                                  setdownloadid(quot._id);
                                  seteditContent(quot.templatedata);
                                  setTemplateid(quot.templateid);
                                } else {
                                  setFiles();
                                  setContent(quot.templatedata);
                                  setsendid(null);
                                }
                                setisHistoryOpen(!isHistoryOpen);
                                setselectedTemplate("");
                              }}
                            >
                              <div className="flex items-center">
                                <span>{quot.quotationId ?? quot.QuotationId ?? quot.quotationNumber ?? quot.leadid ?? "—"}</span>
                                <span>_v{index + 1}</span>
                              </div>
                            </div>
                          ))
                        ) : (
                          <div className="p-2 text-center text-gray-500">
                            No quotation
                          </div>
                        );
                      })()}
                    </div>
                  )}
                                </div>
                            </div>

                            <div className="flex gap-2 justify-end align-bottom items-end">
                                <button
                                    onClick={handleGenerate}
                                    className="p-1.5  text-sm rounded-md bg-[#e32278] text-white inline-block"
                                >
                                    {IsEdit ? "Save" : "Generate"}
                                </button>
                                {files && (
                                    <button
                                        onClick={handleedit}
                                        className="px-3.5 py-1.5  text-sm rounded-md bg-[#e32278] text-white inline-block"
                                    >
                                        Edit
                                    </button>
                                )}
                                <button
                                    disabled={!files}
                                    onClick={() => handleDownload()}
                                    className={`${!files ? "cursor-not-allowed bg-gray-400" : "cursor-pointer bg-[#e32278]"} px-2 py-1 mr-1 mt-2 text-sm rounded-md  text-white inline-block`}
                                >
                                    Download
                                </button>
                                <button
                                    disabled={!files}
                                    onClick={handleSend}
                                    className={`${!files ? "cursor-not-allowed bg-gray-400" : "cursor-pointer bg-[#e32278]"} px-2 py-1 text-sm rounded-md ] text-white inline-block`}
                                >
                                    Send Email
                                </button>
                            </div>
                        </div>
                        <div className="w-full min-h-[5rem] max-h-[24rem] overflow-y-scroll p-2 border mt-2">
                            {sendid ? (
                                <FileViewer files={files} />
                            ) : (
                                <div className="preview w-full shadow-[inset_0_0_10px_rgba(0,0,0,0.3)]">
                                    {content ? (
                                        <div className="flex h-[20rem]">
                                            <div className="w-full">
                                                <JoditEditor
                                                    ref={editor}
                                                    value={content}
                                                    config={config}
                                                />
                                            </div>
                                        </div>
                                    ) : (
                                        <div>No Template</div>
                                    )}
                                    {content && 
                                        (showfile?.[0]?.message ===
                                            "No files available for the given Lead ID" ||
                                            showfile?.files?.length === 0 ? (
                                            <div>No Attachament</div>
                                        ) : (
                                            <FilesViewer showfile={showfile} />
                                        ))}
                                </div>
                            )}
                        </div>
                    </div>
                </div> */}
            </div>
        </>
    );
};

export default QuotationWizardBasePlan;
